import {
  useOrdersStore,
  useInitializeOrderPermissions,
} from "./use-orders-store";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/atoms/button";
import { CreateOrderModal } from "@/pages/orders/partials/CreateOrderModal";
import { Plus } from "lucide-react";
import OrderTable from "./order-table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/atoms/card";
import Pagination from "@/components/atoms/Pagination";
import OrderStatusFilter from "./order-status-filter";
import OrderBranchFilter from "./order-branch-filter";
import OrderBrandFilter from "./order-brand-filter";
import OrderSearchFilter from "./order-search-filter";
import RealtimeStatusIndicator from "@/components/orders/RealtimeStatusIndicator";
import { useEffect } from "react";
import { toastHandler } from "@/lib/ToastHandler";

export default function OrderAll() {
  // Initialize permissions in the store
  useInitializeOrderPermissions();

  const currentPage = useOrdersStore((state) => state.currentPage);
  const totalPages = useOrdersStore((state) => state.totalPages);
  const totalCount = useOrdersStore((state) => state.totalCount);
  const setCurrentPage = useOrdersStore((state) => state.setCurrentPage);

  // Filter states and setters
  const statusFilter = useOrdersStore((state) => state.statusFilter);
  const branchFilter = useOrdersStore((state) => state.branchFilter);
  const brandFilter = useOrdersStore((state) => state.brandFilter);
  const trackingIdFilter = useOrdersStore((state) => state.trackingIdFilter);

  const clearAllFilters = useOrdersStore((state) => state.clearAllFilters);

  const handleReload = () => {
    useOrdersStore.getState().fetchPaginatedOrdersCurrentDay(currentPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Check if any filters are active (not at default values)
  const hasActiveFilters =
    statusFilter !== "all" ||
    branchFilter !== "all" ||
    brandFilter !== "all" ||
    trackingIdFilter.trim() !== "";

  const handleClearFilters = () => {
    clearAllFilters();
  };

  // Listen to global realtime update events and show a toast
  // NOTA: Para evitar mostrar el toast varias veces seguidas, usamos un flag simple de debounce y un timer.

  useEffect(() => {
    let debounce = false;
    let timer: NodeJS.Timeout | null = null;

    const onUpdate = () => {
      if (!debounce) {
        debounce = true;
        toastHandler({
          title: "Órdenes actualizadas",
          description: "Se recibió una actualización en tiempo real.",
          variant: "default",
        });
        timer = setTimeout(() => {
          debounce = false;
        }, 1000); // Ignore subsequent events within 1 segundo
      }
    };
    window.addEventListener(
      "orders-realtime:update",
      onUpdate as EventListener,
    );
    return () => {
      window.removeEventListener(
        "orders-realtime:update",
        onUpdate as EventListener,
      );
      if (timer) clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <PageHeader
        title="Órdenes"
        description="Revisa los estados de las órdenes del día"
        children={
          <CreateOrderModal
            trigger={
              <Button
                size="sm"
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 flex items-center justify-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                Nueva orden
              </Button>
            }
            onOrderCreated={handleReload}
          />
        }
      />
      <OrderStatusFilter />
      <div className="p-4">
        <Card className="bg-white rounded-lg">
          <CardHeader>
            <div className="flex flex-col justify-between">
              <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span>Órdenes del día</span>
                  {totalCount > 0 && (
                    <span className="text-sm font-normal text-gray-500">
                      ({totalCount} total)
                    </span>
                  )}
                  <RealtimeStatusIndicator />
                </div>
                {/* Pagination for mobile - top position */}
                {totalPages > 1 && (
                  <div className="sm:hidden">
                    <Pagination
                      totalPages={totalPages}
                      currentPage={currentPage}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </CardTitle>

              {/* Filters Section */}
              <div className="space-y-3 mt-4 xl:space-y-0 xl:mt-4 xl:flex xl:items-end xl:gap-4">
                {/* Search Filter - Full width on mobile, first column on xl */}
                <div className="w-full xl:w-1/3">
                  <OrderSearchFilter />
                </div>

                {/* Brand, Branch, Clear Filters - Responsive layout, row on xl */}
                <div className="flex flex-col xs:flex-row gap-2 xs:gap-3 xl:flex-row xl:gap-4 xl:w-2/3">
                  <div className="flex-1 min-w-0">
                    <OrderBrandFilter />
                  </div>
                  <div className="flex-1 min-w-0">
                    <OrderBranchFilter />
                  </div>
                  {hasActiveFilters && (
                    <div className="flex-shrink-0">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleClearFilters}
                        className="border-red-300 h-10 border text-red-300 w-full xs:w-auto sm:rounded-lg"
                      >
                        <span className="xs:hidden">Limpiar Filtros</span>
                        <span className="hidden xs:inline">
                          Limpiar Filtros
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <OrderTable />
            {totalPages > 1 && (
              <div className="flex flex-col justify-center pt-4">
                <Pagination
                  totalPages={totalPages}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
                <p className="text-sm text-gray-500">
                  Página {currentPage} de {totalPages}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
